import axios from './axiosConfig'
import type { ApiRequestOptions } from './requestOptions'
import type { FinancialPaymentMethod } from './financialAccounts'

export type RefundPreview = {
 total: number
 subtotal: number
 tax: number
 delivery_fee: number
 refund_base_total: number
 loyalty_value_restored: number
 loyalty_points_restored: number
 service_charge_refunded: number
 service_charge_retained: number
 non_reserving_preview: boolean
}

export type SalePaymentType = FinancialPaymentMethod | 'credit' | 'layaway' | 'split' | 'wallet'

interface SaleItemData {
 product_id: number
 quantity: number
 unit_price: number
 discount?: number
 unit_id?: number | null
 variant_id?: string | null
 selected_options?: Array<{ product_option_group_id: number; option_ids: number[] }>
 client_cart_line_id?: string | null
 kitchen_route_type?: 'screen' | 'printer' | 'none' | null
 kitchen_station_id?: string | null
 kitchen_printer_id?: string | null
 kitchen_printed_quantity?: number
 kitchen_printed_at?: string | null
}

interface SaleData {
 client_uuid?: string
 checkout_intent_id?: string
 idempotency_key?: string
 price_defining_options_version?: number
 customer_id?: number | null
 items: SaleItemData[]
 subtotal: number
 tax?: number
 discount?: number
 promotion_discount?: number
 total: number
 paid: number
 payment_type: SalePaymentType
 notes?: string
 warehouse_id?: string
 shift_id?: string | null
 pos_register_id?: string | null
 register_session_id?: string | null
 sale_date?: string
 order_type?: 'dine_in' | 'takeaway' | 'delivery'
 dining_table_id?: string | null
 delivery_fee?: number
 delivery_fee_override_reason?: string | null
 delivery_contract_version?: 'delivery_pricing_v1' | 'delivery_pricing_v2'
 service_charge?: number
 delivery_address?: string
 delivery_phone?: string
 delivery_zone_id?: string | null
 customer_address_id?: string | null
 delivery_collection_method?: 'cashier' | 'driver'
 coupon_id?: string | null
 coupon_discount?: number
 loyalty_points_redeemed?: number
 loyalty_discount?: number
 payment_lines?: Array<{
  financial_account_id?: string | null
  vault_id?: string | null
  amount: number
  payment_method?: string
  reference?: string | null
  client_line_id?: string
 }> | null
 layaway_terms?: {
 base_total: number
 markup_percent: number
 term_months: number
 down_payment_amount: number
 first_due_date: string
 } | null
}

interface HoldCartData {
 name?: string
 cart_items: any[]
 customer_id?: number
 subtotal: number
 tax?: number
 discount?: number
 cart_discount?: number
 promotion_discount?: number
 coupon_id?: string | null
 coupon_discount?: number
 coupon_snapshot?: Record<string, unknown> | null
 total: number
 paid?: number
 payment_type: SalePaymentType
 notes?: string
}

interface RequestOptions {
 xBranchId?: string | null
}

export const salesAPI = {
 getAll: async (params?: any, options?: ApiRequestOptions) => {
 const response = await axios.get('/pos/sales', { params, ...options })
 return response.data
 },

 soldItems: async (params?: any) => {
 const response = await axios.get('/pos/sales/sold-items', { params })
 return response.data
 },

 getById: async (id: number, options?: ApiRequestOptions) => {
 const response = await axios.get(`/pos/sales/${id}`, options)
 return response.data
 },

 create: async (data: SaleData) => {
 const response = await axios.post('/pos/sales', data)
 return response.data
 },

  refund: async (
    id: number,
    data?: {
      reason?: string
      cash_refund_source?: 'drawer' | 'vault'
      refund_channel?: string
      refund_vault_id?: string
      refund_financial_account_id?: string
      alternative_refund_reason?: string
      client_uuid?: string
      pos_register_id?: string
      register_session_id?: string
    },
  ) => {
    const response = await axios.post(`/pos/sales/${id}/refund`, data ?? {})
    return response.data
  },

 print: async (
  id: number,
  options?: RequestOptions & { mode?: 'original' | 'return' | 'current'; refundId?: number },
 ) => {
 const config: {
  headers?: Record<string, string>
  params?: Record<string, string | number>
 } = {}
 if (options?.xBranchId) {
 config.headers = { 'X-Branch-Id': options.xBranchId }
 }
 const params: Record<string, string | number> = {}
 if (options?.mode) params.mode = options.mode
 if (options?.refundId != null) params.refund_id = options.refundId
 if (Object.keys(params).length > 0) config.params = params
 const response = await axios.post(`/pos/sales/${id}/print`, undefined, config)
 return response.data
 },

 timeline: async (id: number) => {
  const response = await axios.get(`/pos/sales/${id}/timeline`)
  return response.data
 },

 // Hold Cart methods
 holdCart: async (data: HoldCartData) => {
 const response = await axios.post('/pos/hold-cart', data)
 return response.data
 },

 getHoldCarts: async () => {
 const response = await axios.get('/pos/hold-carts')
 return response.data
 },

 restoreCart: async (id: number) => {
 const response = await axios.post(`/pos/restore-cart/${id}`)
 return response.data
 },

 deleteHoldCart: async (id: number) => {
 const response = await axios.delete(`/pos/hold-cart/${id}`)
 return response.data
 },

 partialRefund: async (saleId: number, data: { client_uuid?: string; items: Array<{ sale_item_id: number; quantity: number; restock?: boolean }>; reason?: string; notes?: string; refund_method?: 'cash' | 'wallet' | 'original_account' | 'alternative_account'; cash_refund_source?: 'drawer' | 'vault'; refund_vault_id?: string; refund_financial_account_id?: string; refund_channel?: 'cash' | 'card' | 'instapay' | 'electronic_wallet' | 'vodafone_cash' | 'bank_transfer' | 'payment_gateway'; alternative_refund_reason?: string }) => {
 const response = await axios.post(`/pos/sales/${saleId}/partial-refund`, data)
 return response.data
 },

 previewPartialRefund: async (saleId: number, items: Array<{ sale_item_id: number; quantity: number }>) => {
 const response = await axios.post(`/pos/sales/${saleId}/partial-refund/preview`, { items })
 return response.data as { data?: RefundPreview; message?: string }
 },
}
